import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap, catchError } from 'rxjs/operators';
import { LeaderService } from '../services/leader.service';

@Component({
  selector: 'user-autocomplete',
  standalone: false,
  styles: [`
  .form-field {
  width: 100%;
  font-family: 'Poppins', sans-serif;
}

  `],
  template: `
    <mat-form-field class="form-field " appearance="outline">
      <mat-label>{{ label }}</mat-label>
      <mat-chip-grid #chipGrid aria-label="Attendee selection">
        <mat-chip-row *ngFor="let user of selectedUsers">
          {{ user.displayName }}
          <button matChipRemove [attr.aria-label]="'remove ' + user.displayName" (click)="removeUser(user)">
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip-row>
      </mat-chip-grid>

      <input name="attendeeInput" id="attendeeInput" [placeholder]="placeholder" [matChipInputFor]="chipGrid"
        [matAutocomplete]="auto" (input)="onSearchChange($event)" [disabled]="isTaskPage && selectedUsers.length >= 1" required>

      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onUserSelected($event)">
        <mat-option *ngIf="isLoading">
          <div class="spinner" *ngIf="isLoading">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </mat-option>

        <mat-option *ngIf="!isLoading && filteredUsers && filteredUsers.length === 0" disabled>
          No search results.
        </mat-option>

        <mat-option *ngFor="let user of filteredUsers" [value]="user">
          <div class="user-option">
            <span class="user-name">{{ user.displayName }}</span>
            <small class="user-email"> ({{ user.email }})</small>
          </div>
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
 
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserAutocompleteComponent),
      multi: true
    }
  ]
})
export class UserAutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string = 'Attendees';
  @Input() placeholder: string = 'Add User...';
  @Input() searchFn!: (searchText: string) => any;
  @Output() userSelected = new EventEmitter<any>();
  @Output() userRemoved = new EventEmitter<any>();
  @Input() pageName: string = '';
  get isTaskPage(): boolean {
    return this.pageName === 'task';
  }
  selectedUsers: any[] = [];

  // ControlValueAccessor implementation
  private onChange: any = () => { };
  private onTouched: any = () => { };

  writeValue(value: any): void {
    this.selectedUsers = Array.isArray(value) ? value : [];
    // Emit value to form on init or external set
    this.onChange(this.selectedUsers);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Optionally handle disabled state
  }
  constructor(public leaderService: LeaderService) { }

  searchSubject = new Subject<string>();
  searchSubscription?: Subscription;
  filteredUsers: any[] = [];
  isLoading = false;

  ngOnInit() {
  }
  setupSearchPipeline(searchText: string) {
    this.leaderService.searchUserDataList(searchText).pipe(
      debounceTime(300),
    ).subscribe((res: any) => 
       this.userList(res)
    );
  }
userList(res: any) {
    const allUserSuggestions = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res)
        ? res
        : [];

    this.filteredUsers = allUserSuggestions.filter((u: any) =>
      (u?.email || u?.email || '').toLowerCase()
    );
  }
  onSearchChange(event: any) {
    this.searchSubject.next(event.target.value);
    if (event.target.value.length > 2) {
      this.setupSearchPipeline(event.target.value);
    }
  }
  onUserSelected(event: any): void {
    const user = event.option.value;
    if (this.isTaskPage) {
      // Only allow one user to be selected
      this.selectedUsers = [user];
      this.userSelected.emit(user);
      this.onChange([...this.selectedUsers]);
    } else {
      if (!this.selectedUsers.find(u => u.id === user.id)) {
        this.selectedUsers.push(user);
        this.userSelected.emit(user);
        this.onChange([...this.selectedUsers]);
      }
    }
    const inputElement = document.getElementById('attendeeInput') as HTMLInputElement;
    if (inputElement) inputElement.value = '';
    this.filteredUsers = [];
    this.onTouched();
  }

  removeUser(user: any) {
    const index = this.selectedUsers.indexOf(user);
    if (index >= 0) {
      this.selectedUsers.splice(index, 1);
      this.userRemoved.emit(user);
      this.onChange([...this.selectedUsers]);
    }
    this.onTouched();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }
}
